import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import Spinner from './Spinner';
import {
    AlertTriangleIcon,
    ArrowRightIcon,
    CartIcon,
    CheckCheckIcon,
    EyeIcon,
    LightbulbIcon,
    LinkIcon,
    ListIcon,
    PlusIcon,
    RefreshIcon,
    XIcon,
} from './icons';
import {pluralOutfits} from '../utils/plural';

/** Атрибут спецификации (цвет/материал/тепло/формальность) — код + русское имя для чипа. */
export interface SpecAttribute {
    code: string;
    nameRu: string;
}

/**
 * Руководство по покупке (feature purchase-guidance): почему сейчас, каким должен быть предмет,
 * что проверить при выборе, чего избегать. Пустой блок не рендерится.
 */
export interface PurchaseGuidance {
    whyNow: string[];
    mustHave: string[];
    checkpoints: string[];
    avoid: string[];
}

/** Спецификация покупки по функциональной роли (MR-2 purchase-spec-search-links). */
export interface PurchaseSpec {
    id: string | null;
    roleCode: string;
    gapKey: string;
    chosenCategoryId: string | null;
    chosenCategoryName: string | null;
    palette: SpecAttribute[];
    materials: SpecAttribute[];
    warmth: SpecAttribute[];
    formality: SpecAttribute[];
    rationale: string | null;
    /** Руководство по выбору; additive-поле — старый бэкенд его не отдаёт. */
    guidance?: PurchaseGuidance | null;
    compatibleItemsCount: number;
    priceMin: number | null;
    priceMax: number | null;
    searchQuery: string | null;
    newOutfitEstimate: number | null;
    status: 'READY' | 'FALLBACK';
}

/** Поисковая ссылка маркетплейса. */
export interface MarketplaceLink {
    code: string;
    name: string;
    url: string;
}

/** Ответ {@code GET /api/shopping/shortlist?roleCode=}. */
export interface ShortlistResponse {
    roleCode: string;
    roleNameRu: string | null;
    spec: PurchaseSpec;
    marketplaceLinks: MarketplaceLink[];
    budgetStatus: string | null;
    remainingBudget: number | null;
    aiEnriched: boolean;
}

interface PurchaseSpecPanelProps {
    /** Ключ рекомендации (формат {@code FILL_GAP:<roleCode>}). */
    recKey: string;
    /** Тип рекомендации (для {@code from-shortlist}); здесь всегда {@code FILL_GAP}. */
    recType: string;
    /** Код функциональной роли (из ключа карточки). */
    roleCode: string;
    /** Пункт добавлен в список — рекомендация принята backend'ом; родитель перезагружает ленту. */
    onAccepted: () => void;
    /** «Не интересно» — родитель фиксирует решение DISMISSED. */
    onDismiss: () => void;
}

const money = (n: number | null): string =>
    n == null ? '—' : `${Math.round(n).toLocaleString('ru-RU')} ₽`;

type Status = 'loading' | 'error' | 'ready';

const chipClass = 'px-2 py-0.5 rounded-full bg-gray-100 dark:bg-ink-700 text-xs text-gray-600 dark:text-gray-300';

/**
 * Блок руководства по покупке: заголовок с иконкой и список пунктов. Пустой список не
 * рендерится вовсе — пустой заголовок хуже отсутствующего блока.
 */
const GuidanceBlock: React.FC<{
    title: string;
    items: string[] | undefined;
    icon: React.ReactNode;
    testId: string;
}> = ({title, items, icon, testId}) => {
    if (!items || items.length === 0) {
        return null;
    }
    return (
        <div data-testid={testId}>
            <h4 className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                {icon} {title}
            </h4>
            <ul className="mt-1 space-y-1 pl-1 text-sm text-gray-600 dark:text-gray-300">
                {items.map(item => (
                    <li key={item} className="flex gap-2">
                        <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">•</span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/**
 * Панель подбора покупки для рекомендации FILL_GAP (MR-2 purchase-spec-search-links):
 * спецификация «что искать» (палитра-чипы, материалы, тепло/формальность, вилка цены,
 * «даст ~N образов», «сочетается с N вещами») + поисковые ссылки маркетплейсов и добавление
 * в список покупок с выбранным маркетплейсом. FALLBACK показывается бейджем «без AI».
 */
const PurchaseSpecPanel: React.FC<PurchaseSpecPanelProps> = ({recKey, recType, roleCode, onAccepted, onDismiss}) => {
    const [status, setStatus] = useState<Status>('loading');
    const [data, setData] = useState<ShortlistResponse | null>(null);
    const [selectedMarketplace, setSelectedMarketplace] = useState<string | null>(null);
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);
    const [actionError, setActionError] = useState(false);

    const load = useCallback(async () => {
        setStatus('loading');
        try {
            const api = await createAxiosInstance();
            const res = await api.get<ShortlistResponse>('/shopping/shortlist', {params: {roleCode}});
            setData(res.data);
            setSelectedMarketplace(res.data.marketplaceLinks[0]?.code ?? null);
            setStatus('ready');
        } catch (e) {
            setStatus('error');
        }
    }, [roleCode]);

    useEffect(() => {
        load();
    }, [load]);

    const addToList = async () => {
        if (!data) {
            return;
        }
        setAdding(true);
        setActionError(false);
        const spec = data.spec;
        const title = [spec.chosenCategoryName || data.roleNameRu, spec.palette[0]?.nameRu]
            .filter(Boolean).join(' · ');
        try {
            const api = await createAxiosInstance();
            await api.post('/shopping/from-shortlist', {
                recKey,
                recType,
                title,
                categoryId: spec.chosenCategoryId,
                marketplaceCode: selectedMarketplace,
                note: spec.searchQuery,
            });
            setAdded(true);
            // from-shortlist атомарно фиксирует ACCEPTED → рекомендация исчезает из ленты.
            onAccepted();
        } catch (e) {
            setActionError(true);
        } finally {
            setAdding(false);
        }
    };

    const spec = data?.spec;

    return (
        <div
            className="mt-2 rounded-xl border border-gray-200 dark:border-ink-700 bg-gray-50 dark:bg-ink-800 p-3"
            data-testid="purchase-spec-panel"
        >
            {status === 'loading' && (
                <div data-testid="spec-loading" className="py-2">
                    <Spinner/>
                </div>
            )}

            {status === 'error' && (
                <div className="text-center py-4 space-y-3" data-testid="spec-error">
                    <p className="text-gray-600 dark:text-gray-300">Не удалось загрузить подбор покупки</p>
                    <button
                        type="button"
                        onClick={load}
                        className="inline-flex min-h-[44px] items-center gap-2 px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                    >
                        <RefreshIcon size={18}/> Повторить
                    </button>
                </div>
            )}

            {status === 'ready' && data && spec && (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100" data-testid="spec-category">
                            {spec.chosenCategoryName || data.roleNameRu}
                        </span>
                        {!data.aiEnriched && (
                            <span
                                className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-xs text-amber-700 dark:text-amber-300"
                                title="AI недоступен — подбор построен по правилам, без обогащения"
                                data-testid="spec-fallback-badge"
                            >
                                без AI
                            </span>
                        )}
                    </div>

                    {spec.rationale && (
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="spec-rationale">
                            {spec.rationale}
                        </p>
                    )}

                    <div className="flex flex-wrap gap-2" data-testid="spec-attributes">
                        {spec.palette.map(a => (
                            <span key={`color-${a.code}`} className={chipClass} data-testid="spec-color-chip">
                                {a.nameRu}
                            </span>
                        ))}
                        {spec.materials.map(a => (
                            <span key={`material-${a.code}`} className={chipClass}>{a.nameRu}</span>
                        ))}
                        {spec.warmth.map(a => (
                            <span key={`warmth-${a.code}`} className={chipClass}>{a.nameRu}</span>
                        ))}
                        {spec.formality.map(a => (
                            <span key={`formality-${a.code}`} className={chipClass}>{a.nameRu}</span>
                        ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="spec-facts">
                        <span className="font-semibold text-gray-900 dark:text-gray-100" data-testid="spec-price">
                            {money(spec.priceMin)} – {money(spec.priceMax)}
                        </span>
                        {spec.newOutfitEstimate != null && spec.newOutfitEstimate > 0 && (
                            <span className="text-gray-600 dark:text-gray-300" data-testid="spec-outfits">
                                даст ~{pluralOutfits(spec.newOutfitEstimate)}
                            </span>
                        )}
                        {spec.compatibleItemsCount > 0 && (
                            <span className="text-gray-600 dark:text-gray-300" data-testid="spec-compatible">
                                сочетается с {spec.compatibleItemsCount} вашими вещами
                            </span>
                        )}
                    </div>

                    {(data.budgetStatus || data.remainingBudget != null) && (
                        <div className="text-sm text-gray-600 dark:text-gray-300" data-testid="spec-budget-context">
                            {data.remainingBudget != null
                                ? `Остаток бюджета: ${money(data.remainingBudget)}`
                                : 'Бюджет не задан'}
                        </div>
                    )}

                    {spec.guidance && (
                        <div className="space-y-3 border-t border-gray-200 dark:border-ink-700 pt-3"
                             data-testid="spec-guidance">
                            <GuidanceBlock
                                title="Почему это стоит купить"
                                items={spec.guidance.whyNow}
                                icon={<LightbulbIcon size={16}/>}
                                testId="guidance-why-now"
                            />
                            <GuidanceBlock
                                title="Каким должен быть предмет"
                                items={spec.guidance.mustHave}
                                icon={<ListIcon size={16}/>}
                                testId="guidance-must-have"
                            />
                            <GuidanceBlock
                                title="На что смотреть при выборе"
                                items={spec.guidance.checkpoints}
                                icon={<EyeIcon size={16}/>}
                                testId="guidance-checkpoints"
                            />
                            <GuidanceBlock
                                title="Избегать"
                                items={spec.guidance.avoid}
                                icon={<AlertTriangleIcon size={16}/>}
                                testId="guidance-avoid"
                            />
                        </div>
                    )}

                    {data.marketplaceLinks.length > 0 && (
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap" data-testid="spec-marketplaces">
                            {data.marketplaceLinks.map(link => (
                                <a
                                    key={link.code}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => setSelectedMarketplace(link.code)}
                                    className={`inline-flex min-h-[44px] items-center justify-center gap-2 px-4 rounded-xl border text-gray-700 dark:text-gray-200 hover:border-brand-500 ${
                                        selectedMarketplace === link.code
                                            ? 'border-brand-500'
                                            : 'border-gray-200 dark:border-ink-700'
                                    }`}
                                    data-testid="spec-marketplace-link"
                                >
                                    <LinkIcon size={18}/> {link.name}
                                </a>
                            ))}
                        </div>
                    )}

                    <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                            type="button"
                            onClick={addToList}
                            disabled={adding || added}
                            className="inline-flex min-h-[44px] items-center justify-center gap-2 px-4 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                            data-testid="spec-add"
                        >
                            {added
                                ? (<><CheckCheckIcon size={18}/> Добавлено</>)
                                : (<><PlusIcon size={18}/> Добавить в список</>)}
                        </button>
                        <button
                            type="button"
                            onClick={onDismiss}
                            className="inline-flex min-h-[44px] items-center justify-center gap-2 px-4 rounded-xl border border-gray-200 dark:border-ink-700 text-gray-500 dark:text-gray-400 hover:border-brand-500"
                            data-testid="spec-dismiss"
                        >
                            <XIcon size={18}/> Не интересно
                        </button>
                    </div>

                    {added && (
                        <Link
                            to="/shopping"
                            className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-brand-600 dark:text-brand-400 underline hover:no-underline"
                            data-testid="spec-open-shopping"
                        >
                            Открыть список покупок <ArrowRightIcon size={16}/>
                        </Link>
                    )}

                    {actionError && (
                        <p className="text-rose-600 dark:text-rose-400 text-sm" data-testid="spec-action-error">
                            Не удалось добавить в список. Попробуйте ещё раз.
                        </p>
                    )}

                    {spec.searchQuery && (
                        <p className="text-xs text-gray-400 dark:text-gray-500" data-testid="spec-query">
                            <CartIcon size={14} className="inline mr-1"/>
                            Поисковый запрос: {spec.searchQuery}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default PurchaseSpecPanel;
